'use client'

import { REGEXP_ONLY_DIGITS } from 'input-otp'
import { useRouter } from 'next/navigation'
import { useEffect, useRef, useState, useTransition } from 'react'

import { authClient } from '@/auth-client'
import {
    InputOTP,
    InputOTPGroup,
    InputOTPSeparator,
    InputOTPSlot,
} from '@/components/ui/input-otp'
import type { EnabledProviders } from '@/lib/auth/enabled-providers'

const CODE_LENGTH = 6
// Mirrors the plugin's allowedAttempts (auth.ts); the client keeps its own
// tally so the lockout copy shows on the third miss, without a fourth request.
const ALLOWED_ATTEMPTS = 3

const LOCKED_COPY = 'Too many tries. Send a new code below.'
const EXPIRED_COPY = 'This code has expired. Send another below.'
const FALLBACK_COPY = 'Couldn’t sign you in. Send a new code below.'

function attemptsLeftCopy(left: number): string {
    return `That code isn’t right. ${left} ${left === 1 ? 'attempt' : 'attempts'} left.`
}

const OUTLINED_BUTTON =
    'flex w-full items-center justify-center border border-rule bg-paper px-6 py-4 font-serif text-base text-ink transition-colors hover:border-ink hover:bg-surface-elevated focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none'

// reason: text-link affordances hover to text-action (chrome-strip pattern);
// vermilion only on hover, never at rest — preserves the §6 budget.
const TEXT_LINK =
    'inline-flex items-center gap-1 font-serif text-base tracking-wide text-muted-fg italic transition-colors hover:text-action focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-focus-ring disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none'

/**
 * Signin auth-form client island. Holds every piece of interactive state
 * for the signin page: OAuth button handlers, email form expansion, the
 * code send, the after-submit body swap with the code field, the attempts
 * tally, the resend cooldown.
 *
 * Single client island over per-button micro-islands — over-segments the
 * shared state (typed email, cooldown timer, expanded flag, submitted flag).
 *
 * Spec: docs/superpowers/specs/2026-05-13-signin-mini-spec.md §1.1 (rows
 * 3-4), §1.4 (after-submit state with the code field), §2.3/§2.4 (copy),
 * §4.6 (client state machine), §5.3/§5.4 (states).
 */
export function AuthForm({
    enabledProviders,
    initialFormExpanded,
}: {
    enabledProviders: EnabledProviders
    initialFormExpanded: boolean
}) {
    const router = useRouter()
    const [expanded, setExpanded] = useState(initialFormExpanded)
    const [email, setEmail] = useState('')
    const [pending, startTransition] = useTransition()
    const [submitted, setSubmitted] = useState(false)
    const [submittedEmail, setSubmittedEmail] = useState('')
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [code, setCode] = useState('')
    const [codeError, setCodeError] = useState<string | null>(null)
    const [attemptsLeft, setAttemptsLeft] = useState(ALLOWED_ATTEMPTS)
    const [checking, setChecking] = useState(false)
    const [expired, setExpired] = useState(false)
    const [resending, setResending] = useState(false)
    const [cooldown, setCooldown] = useState(0)
    const [oauthPending, setOauthPending] = useState<
        'google' | 'discord' | null
    >(null)
    const [oauthError, setOauthError] = useState<string | null>(null)
    const [resendError, setResendError] = useState<string | null>(null)
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const codeRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current)
        }
    }, [])

    const locked = attemptsLeft === 0

    // After a wrong code the field re-enables: take focus back with the typed
    // value selected, so the retype overwrites it (spec §5.4).
    useEffect(() => {
        if (!checking && !locked && !expired && submitted) {
            codeRef.current?.focus()
            if (codeError !== null) codeRef.current?.select()
        }
    }, [checking, locked, expired, submitted, codeError])

    const startCooldown = () => {
        setCooldown(60)
        if (intervalRef.current) clearInterval(intervalRef.current)
        intervalRef.current = setInterval(() => {
            setCooldown((n) => {
                if (n <= 1) {
                    if (intervalRef.current) clearInterval(intervalRef.current)
                    return 0
                }
                return n - 1
            })
        }, 1000)
    }

    const resetCodeState = () => {
        setCode('')
        setCodeError(null)
        setAttemptsLeft(ALLOWED_ATTEMPTS)
        setExpired(false)
    }

    const handleOAuth = (provider: 'google' | 'discord') => {
        setOauthError(null)
        setOauthPending(provider)
        authClient.signIn
            .social({ provider, callbackURL: '/journey' })
            .then(() => {
                if (typeof window !== 'undefined') {
                    localStorage.setItem('hasAlreadySignedIn', 'true')
                }
            })
            .catch((error: unknown) => {
                const message =
                    error instanceof Error ? error.message : 'Sign-in failed'
                setOauthError(message)
                setOauthPending(null)
            })
    }

    const sendCode = async (to: string): Promise<boolean> => {
        const result = await authClient.emailOtp.sendVerificationOtp({
            email: to,
            type: 'sign-in',
        })
        return !result.error
    }

    const handleSend = (e: React.FormEvent) => {
        e.preventDefault()
        const trimmed = email.trim()
        if (trimmed.length === 0) return
        setSubmitError(null)
        startTransition(async () => {
            if (!(await sendCode(trimmed))) {
                setSubmitError('Couldn’t send the code. Try again.')
                return
            }
            setSubmittedEmail(trimmed)
            setSubmitted(true)
            resetCodeState()
            startCooldown()
        })
    }

    const handleResend = () => {
        if (cooldown > 0 || resending || checking) return
        setResendError(null)
        setResending(true)
        void (async () => {
            const ok = await sendCode(submittedEmail)
            setResending(false)
            if (!ok) {
                setResendError('Couldn’t send the code. Try again.')
                return
            }
            // A new send rotates the code (plugin default), so the old one
            // and its misses are void.
            resetCodeState()
            startCooldown()
            codeRef.current?.focus()
        })()
    }

    const handleUseDifferent = () => {
        setSubmitted(false)
        setSubmittedEmail('')
        setEmail('')
        setResendError(null)
        resetCodeState()
        if (intervalRef.current) clearInterval(intervalRef.current)
        setCooldown(0)
    }

    const codeComplete = code.length === CODE_LENGTH

    const handleCheck = (e: React.FormEvent) => {
        e.preventDefault()
        if (!codeComplete || locked || expired || checking) return
        setCodeError(null)
        setChecking(true)
        void (async () => {
            const result = await authClient.signIn.emailOtp({
                email: submittedEmail,
                otp: code,
            })
            if (!result.error) {
                if (typeof window !== 'undefined') {
                    localStorage.setItem('hasAlreadySignedIn', 'true')
                }
                router.push('/journey')
                return
            }
            setChecking(false)
            switch (result.error.code) {
                case 'INVALID_OTP': {
                    const left = attemptsLeft - 1
                    setAttemptsLeft(left)
                    setCodeError(
                        left === 0 ? LOCKED_COPY : attemptsLeftCopy(left)
                    )
                    break
                }
                case 'OTP_EXPIRED':
                    setExpired(true)
                    setCodeError(EXPIRED_COPY)
                    break
                case 'TOO_MANY_ATTEMPTS':
                    setAttemptsLeft(0)
                    setCodeError(LOCKED_COPY)
                    break
                default:
                    setCodeError(FALLBACK_COPY)
            }
        })()
    }

    if (submitted) {
        return (
            <div className="flex flex-col items-center text-center">
                <h2 className="font-serif text-h2 tracking-tight text-ink">
                    A code is on its way.
                </h2>
                <p className="mt-3 font-serif text-base text-muted-fg italic">
                    Check inbox for {submittedEmail} — or its spam folder.
                    <br />
                    The code expires in 10 minutes.
                </p>

                <form
                    onSubmit={handleCheck}
                    className="mt-6 flex w-full flex-col items-center gap-3"
                >
                    <InputOTP
                        ref={codeRef}
                        maxLength={CODE_LENGTH}
                        pattern={REGEXP_ONLY_DIGITS}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        autoFocus
                        pasteTransformer={(pasted) => pasted.replace(/\D/g, '')}
                        value={code}
                        onChange={(v) => {
                            setCode(v)
                            if (!locked && !expired) setCodeError(null)
                        }}
                        disabled={checking || locked || expired}
                        aria-label="The six-digit code from the email"
                        aria-invalid={codeError !== null}
                    >
                        <InputOTPGroup>
                            {[0, 1, 2].map((i) => (
                                <InputOTPSlot
                                    key={i}
                                    index={i}
                                    aria-invalid={codeError !== null}
                                />
                            ))}
                        </InputOTPGroup>
                        <InputOTPSeparator />
                        <InputOTPGroup>
                            {[3, 4, 5].map((i) => (
                                <InputOTPSlot
                                    key={i}
                                    index={i}
                                    aria-invalid={codeError !== null}
                                />
                            ))}
                        </InputOTPGroup>
                    </InputOTP>
                    {codeError && (
                        <p
                            role="alert"
                            aria-live="polite"
                            className="font-serif text-sm text-error"
                        >
                            {codeError}
                        </p>
                    )}
                    <button
                        type="submit"
                        disabled={
                            !codeComplete || locked || expired || checking
                        }
                        className={OUTLINED_BUTTON}
                    >
                        {checking ? 'Checking…' : 'Continue'}
                    </button>
                </form>

                <div className="mt-8 ml-auto flex flex-col items-end gap-2">
                    <button
                        type="button"
                        onClick={handleResend}
                        disabled={cooldown > 0 || resending || checking}
                        className={TEXT_LINK}
                    >
                        {resending
                            ? 'Sending…'
                            : cooldown > 0
                              ? `Send another in ${cooldown}s…`
                              : 'Send another →'}
                    </button>
                    {resendError && (
                        <p
                            role="alert"
                            className="font-serif text-sm text-error"
                        >
                            {resendError}
                        </p>
                    )}
                    <button
                        type="button"
                        onClick={handleUseDifferent}
                        className={TEXT_LINK}
                    >
                        Use a different email
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-3">
            {oauthError && (
                <p
                    role="alert"
                    className="text-center font-serif text-base whitespace-pre-line text-body-fg italic"
                >
                    {oauthError}
                </p>
            )}

            {enabledProviders.google && (
                <button
                    type="button"
                    onClick={() => handleOAuth('google')}
                    disabled={oauthPending !== null}
                    className={OUTLINED_BUTTON}
                >
                    {oauthPending === 'google'
                        ? 'Signing in…'
                        : 'Continue with Google'}
                </button>
            )}

            {enabledProviders.discord && (
                <button
                    type="button"
                    onClick={() => handleOAuth('discord')}
                    disabled={oauthPending !== null}
                    className={OUTLINED_BUTTON}
                >
                    {oauthPending === 'discord'
                        ? 'Signing in…'
                        : 'Continue with Discord'}
                </button>
            )}

            {enabledProviders.email && (
                <>
                    <div aria-hidden className="my-2 flex items-center gap-3">
                        <span className="h-px flex-1 bg-rule" />
                        <span className="font-serif text-sm tracking-wide text-muted-fg italic">
                            or
                        </span>
                        <span className="h-px flex-1 bg-rule" />
                    </div>

                    {expanded ? (
                        <form
                            onSubmit={handleSend}
                            className="flex flex-col gap-3"
                        >
                            <label htmlFor="signin-email" className="sr-only">
                                Your email address
                            </label>
                            <input
                                id="signin-email"
                                type="email"
                                autoComplete="email"
                                autoFocus
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                disabled={pending}
                                className="w-full border border-rule bg-surface-sunken px-4 py-3 font-serif text-base text-ink placeholder:text-muted-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring disabled:opacity-60"
                            />
                            {submitError && (
                                <p
                                    role="alert"
                                    className="font-serif text-sm text-error"
                                >
                                    {submitError}
                                </p>
                            )}
                            <button
                                type="submit"
                                disabled={pending || email.trim().length === 0}
                                className={OUTLINED_BUTTON}
                            >
                                {pending ? 'Sending…' : 'Send code'}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setExpanded(false)
                                    setEmail('')
                                    setSubmitError(null)
                                }}
                                disabled={pending}
                                className="font-serif text-sm text-muted-fg italic transition-colors hover:text-action focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-focus-ring disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:transition-none"
                            >
                                cancel
                            </button>
                        </form>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setExpanded(true)}
                            className={`${TEXT_LINK} self-center py-3`}
                        >
                            or sign in with email →
                        </button>
                    )}
                </>
            )}
        </div>
    )
}
